import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';

import { jobsConfigApi, type JobCatalogItem } from '@/api/queries/jobsConfig';
import { isSuperAdmin, useTenantStore } from '@/auth/tenantStore';
import { ErrorComponent } from '@/components/common/ErrorComponent';
import { useSetBreadcrumbs } from '@/components/layout/breadcrumbs';
import {
  Button,
  Checkbox,
  Icon,
  Table,
  Tag,
  useToast,
  type TableColumn,
} from '@/components/ui';
import { formatDateTime } from '@/utils/formatDate';

function JobSettingsRoute() {
  useSetBreadcrumbs([{ label: 'Admin' }, { label: 'Jobs', to: '/admin/jobs' }, { label: 'Configuration' }]);

  const navigate = useNavigate();
  const { data: jobs, isPending } = jobsConfigApi.useJobCatalog();
  const update = jobsConfigApi.useUpdateJobConfig();
  const toast = useToast();

  const onToggle = async (job: JobCatalogItem, enabled: boolean) => {
    try {
      await update.mutateAsync({
        key: job.key,
        body: {
          enabled,
          // Keep the current schedule; null means "use the catalog default".
          cron: job.cron === job.defaultCron ? null : job.cron,
          settings: job.settings,
        },
      });
      toast.show({
        title: enabled ? `Enabled "${job.displayName}"` : `Disabled "${job.displayName}"`,
        severity: 'success',
      });
    } catch (err) {
      toast.show({
        title: 'Failed to update job',
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  const columns: TableColumn<JobCatalogItem>[] = [
    {
      id: 'name',
      header: 'Job',
      cell: (j) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 500 }}>{j.displayName}</span>
          <span style={{ fontSize: 12, color: 'var(--text-3)', maxWidth: 480 }}>{j.description}</span>
        </div>
      ),
    },
    {
      id: 'scope',
      header: 'Scope',
      cell: (j) => <Tag tone={j.scope === 'perTenant' ? 'teal' : 'default'}>{j.scope === 'perTenant' ? 'Per tenant' : 'Global'}</Tag>,
      width: 110,
    },
    {
      id: 'schedule',
      header: 'Schedule',
      cell: (j) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <code style={{ fontSize: 12 }}>{j.cron}</code>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            Next: {j.enabled ? (formatDateTime(j.nextExecution) ?? '—') : 'paused'}
          </span>
        </div>
      ),
      width: 200,
    },
    {
      id: 'enabled',
      header: 'Enabled',
      cell: (j) => (
        <Checkbox
          checked={j.enabled}
          onChange={(checked) => onToggle(j, checked)}
          aria-label={`Toggle ${j.displayName}`}
        />
      ),
      width: 90,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Job configuration</h1>
        <span style={{ flex: 1 }} />
        <Button
          variant="secondary"
          leftIcon={<Icon name="refresh" size={13} />}
          onClick={() => navigate({ to: '/admin/jobs', search: { tab: 'processing' } })}
        >
          Monitoring
        </Button>
      </div>
      <Table
        columns={columns}
        rows={jobs ?? []}
        rowKey={(j) => j.key}
        loading={isPending}
        emptyMessage="No configurable jobs."
        onRowClick={(j) => navigate({ to: '/admin/jobs/$key', params: { key: j.key } })}
        rowActions={(j) => (
          <Button
            variant="secondary"
            leftIcon={<Icon name="settings" size={13} />}
            onClick={() => navigate({ to: '/admin/jobs/$key', params: { key: j.key } })}
          >
            Configure
          </Button>
        )}
      />
    </div>
  );
}

export const Route = createFileRoute('/admin/jobs/settings')({
  beforeLoad: () => {
    if (!isSuperAdmin(useTenantStore.getState())) {
      throw redirect({ to: '/contacts', search: { page: 0, pageSize: 25 } });
    }
  },
  component: JobSettingsRoute,
  errorComponent: ErrorComponent,
});
